import { FaPlus, FaMinus, FaRegSave } from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "../../../../../../header/header";
import Navvbar from "../../../../../../header/Navvbar";
import Footer from "../../../../../../footer.js";
import axios from "axios";

function PriceListTable() {
  /* ──────────────────────  Styles  ────────────────────── */
  const tableStyle = {
    borderCollapse: "collapse",
    width: "100%",
    marginTop: "60px",
    fontFamily: "Arial, sans-serif",
  };

  const baseTd = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#fff",
    fontWeight: "normal",
  };

  const alignerBoxStyle = { ...baseTd };

  const headerStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    backgroundColor: "#f4f4f4",
    color: "#333",
    fontWeight: "bold",
  };

  const addButtonStyle = {
    backgroundColor: "#004aad",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    float: "right",
    marginTop: "10px",
    marginRight: "10px",
  };

  const saveButtonStyle = {
    ...addButtonStyle,
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };

  /* ──────────────────────  State  ────────────────────── */
  const [groups, setGroups] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState({}); // "" for missing price
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const tableDataRef = useRef([]);

  /* ──────────────────────  Fetch data  ────────────────────── */
  useEffect(() => {
    const fetchProcedures = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get("https://hms.backend.hiragroupcloud.com/api/procedures/");

        // ---- collect groups from DB ----
        const groupSet = new Set();
        data.forEach((p) => {
          if (p.prices && Array.isArray(p.prices)) {
            p.prices.forEach((pr) => {
              if (pr.pricingFor === "Group" && pr.target) {
                groupSet.add(pr.target);
              }
            });
          }
        });

        const dynamicGroups = Array.from(groupSet).map((n) => ({
          name: n,
          fixed: true,
          key: n.replace(/\s+/g, "").toLowerCase(),
        }));

        setGroups(dynamicGroups);
        setProcedures(data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load procedures");
        setLoading(false);
      }
    };

    fetchProcedures();
  }, []);

  /* ──────────────────────  Build table + original snapshot  ────────────────────── */
  useEffect(() => {
    const rows = procedures.map((proc) => {
      const row = {
        procedure: proc.name || "Unnamed",
        id: proc._id,
        procedureId: proc.procedureId,
      };

      groups.forEach((g) => {
        const priceObj = proc.prices?.find(
          (p) => p.pricingFor === "Group" && p.target === g.name
        );
        // **Only real price → string, otherwise empty**
        const value = priceObj ? String(priceObj.price) : "";
        row[g.key] = value;
      });

      return row;
    });

    setTableData(rows);
    tableDataRef.current = rows;

    // ---- original snapshot (used for change detection) ----
    const origMap = {};
    procedures.forEach((proc) => {
      origMap[proc._id] = {};
      groups.forEach((g) => {
        const priceObj = proc.prices?.find(
          (p) => p.pricingFor === "Group" && p.target === g.name
        );
        origMap[proc._id][g.key] = priceObj ? String(priceObj.price) : "";
      });
    });
    setOriginalData(origMap);
  }, [procedures, groups]);

  /* ──────────────────────  Handlers  ────────────────────── */
  const handlePriceChange = (rowIndex, groupKey, value) => {
    const updated = [...tableData];
    updated[rowIndex][groupKey] = value;
    setTableData(updated);
    tableDataRef.current = updated;
  };

  const handleGroupNameChange = (idx, newName) => {
    const ng = [...groups];
    ng[idx].name = newName;
    ng[idx].key = newName.replace(/\s+/g, "").toLowerCase();
    setGroups(ng);
  };

  const getChangedPrices = () => {
    const changes = [];

    tableDataRef.current.forEach((row) => {
      const procId = row.id;
      const origRow = originalData[procId] || {};

      groups.forEach((group) => {
        const key = group.key;
        const current = row[key] || "";
        const original = origRow[key] || "";

        if (current !== original) {
          // empty cell → send 0 (or you can send null if you prefer)
          const priceVal = current.trim() === "" ? 0 : parseFloat(current) || 0;
          changes.push({
            procedureId: procId,
            groupName: group.name,
            price: priceVal,
          });
        }
      });
    });

    return changes;
  };

  const handleSave = async () => {
    const updates = getChangedPrices();
    if (updates.length === 0) {
      alert("No changes to save");
      return;
    }

    setSaving(true);
    try {
      await axios.post("https://hms.backend.hiragroupcloud.com/api/prices/save-all", { updates });
      alert("Prices saved successfully!");

      // ---- refresh original snapshot after successful save ----
      const newOrig = { ...originalData };
      tableDataRef.current.forEach((row) => {
        newOrig[row.id] = {};
        groups.forEach((g) => {
          newOrig[row.id][g.key] = row[g.key] || "";
        });
      });
      setOriginalData(newOrig);
    } catch (err) {
      console.error(err);
      alert("Failed to save prices");
    } finally {
      setSaving(false);
    }
  };

  /* ──────────────────────  Render  ────────────────────── */
  if (loading)
    return (
      <div style={{ padding: 20, textAlign: "center" }}>Loading...</div>
    );
  if (error)
    return (
      <div style={{ padding: 20, color: "red", textAlign: "center" }}>{error}</div>
    );

  return (
    <>
      <div style={{ marginBottom: "25px" }}>
        <Header />
        <Navvbar />
      </div>

      <div style={{ width: "95%", margin: "0 auto" }}>
        <div className="row">
          {/* Sidebar */}
          <div className="col-md-2" style={{ borderRight: "1px solid #80aaff" }}>
            <h2 style={{ marginLeft: "10px" }}>Settings</h2>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/Tabs"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Lab Details
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "13px",
                }}
              >
                Store
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/usersetting"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Users
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Permission Groups
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/Account"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Accounts
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/Owerviews"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Finances
              </Link>
            </div>
            <div style={{ columnCount: 1 }}>
              <Link
                to="/owersetting"
                style={{
                  textDecoration: "none",
                  fontSize: "17px",
                  color: "#80aaff",
                  marginLeft: "12px",
                }}
              >
                Case Settings
              </Link>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-10">
            <h2
              style={{
                color: "#333",
                margin: "0 0 10px 0",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              Price Lists Groups
            </h2>

            <div
              style={{
                color: "#004aad",
                marginTop: "40px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Details
            </div>

            <div
              style={{
                backgroundColor: "#f4f4f4",
                height: "80px",
                borderTop: "2px solid #004aad",
                position: "relative",
                padding: "10px",
              }}
            >
              <button
                style={addButtonStyle}
                onClick={() =>
                  setGroups([
                    ...groups,
                    {
                      name: "New Group",
                      fixed: false,
                      key: `new_${Date.now()}`,
                    },
                  ])
                }
              >
                <FaPlus /> Add Group
              </button>
            </div>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...headerStyle, width: "200px" }}>
                    Procedure
                  </th>
                  {groups.map((group, idx) => (
                    <th key={group.key} style={headerStyle}>
                      {group.fixed ? (
                        <div>{group.name}</div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: "5px",
                          }}
                        >
                          <input
                            type="text"
                            value={group.name}
                            onChange={(e) =>
                              handleGroupNameChange(idx, e.target.value)
                            }
                            style={{
                              border: "1px solid #ddd",
                              padding: "6px",
                              width: "100px",
                              boxSizing: "border-box",
                              backgroundColor: "#fff",
                              borderRadius: "4px",
                            }}
                          />
                          <FaMinus
                            style={{
                              color: "white",
                              backgroundColor: "#d32f2f",
                              padding: "6px",
                              cursor: "pointer",
                              borderRadius: "4px",
                              alignSelf: "flex-start",
                              fontSize: "14px",
                            }}
                            onClick={() =>
                              setGroups(groups.filter((_, i) => i !== idx))
                            }
                          />
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tableData.map((row, rIdx) => (
                  <tr key={row.id}>
                    <td
                      style={
                        row.procedure.includes("ALIGNERS")
                          ? alignerBoxStyle
                          : baseTd
                      }
                    >
                      {row.procedure}
                    </td>

                    {groups.map((group) => {
                      const cellKey = group.key;
                      const value = row[cellKey] ?? ""; // guaranteed string

                      return (
                        <td
                          key={cellKey}
                          style={
                            row.procedure.includes("ALIGNERS")
                              ? alignerBoxStyle
                              : baseTd
                          }
                        >
                          <input
                            type="text"
                            value={value}
                            onChange={(e) =>
                              handlePriceChange(rIdx, cellKey, e.target.value)
                            }
                            style={{
                              border: "none",
                              width: "100%",
                              background: "transparent",
                              textAlign: "center",
                              outline: "none",
                              fontSize: "14px",
                            }}
                            placeholder=" " /* single space for visual spacing */
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <p
              style={{
                border: "1px solid #ddd",
                color: "#666",
                fontStyle: "italic",
                backgroundColor: "#fff",
                padding: "5px 8px",
                margin: "10px 0",
              }}
            >
              Attributes: ID: Reinforcements
            </p>

            <div
              style={{
                backgroundColor: "#f4f4f4",
                height: "80px",
                position: "relative",
                padding: "10px",
              }}
            >
              <button
                style={saveButtonStyle}
                onClick={handleSave}
                disabled={saving}
              >
                <FaRegSave /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <Footer />
      </div>
    </>
  );
}

export default PriceListTable;